// RegistryPage — the App/Tool Registry admin surface (ticket 6). A PM registers apps, tools, and
// public apps here; Ros then routes people to them (the finder reasons over each entry's description).
//
// Layout mirrors ProjectsPage: a header + "Register" button, a searchable list grouped by kind
// (app / tool / public_app), and per-row delete. Registration is a Dialog whose fields switch on the
// chosen kind: an app adds redirect URIs + scope checkboxes (and shows the returned clientId/secret on
// success); a tool adds a parent-app selector; a public app shows only the common fields.
//
// Backend fetch/auth follows the Ros pattern exactly (registryApi.ts): fetch with credentials:'include'
// and an Authorization: Bearer header built from the shell token. Studio Cyan/Stone tokens throughout.

import { useCallback, useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import MenuItem from '@mui/material/MenuItem'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import WidgetsIcon from '@mui/icons-material/Widgets'
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined'
import BuildIcon from '@mui/icons-material/Build'
import PublicIcon from '@mui/icons-material/Public'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LaunchIcon from '@mui/icons-material/Launch'
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined'
import { useRosShell } from '../RosShellContext'
import { SERIF_HEADING } from '../theme'
import { CURATED_ICONS, resolveIcon } from '../config/registryIcons'
import {
  listRegistryEntries,
  createRegistryEntry,
  generateStudioClientSecret,
  updateRegistryEntry,
  deleteRegistryEntry,
  KNOWN_SCOPES,
  type RegistryEntry,
  type RegistryKind,
  type RegistryAudience,
  type RegistryEntryInput,
  type RegistryEntryUpdate,
  type RegistryCreateResult,
} from './registryApi'
import {useRosFeatures} from '../access/useRosFeatures'

const KIND_META: Record<RegistryKind, { label: string; plural: string; icon: JSX.Element; blurb: string }> = {
  app: {
    label: 'App',
    plural: 'Apps',
    icon: <AppsOutlinedIcon sx={{ fontSize: 20 }} />,
    blurb: 'A first-party or partner app with its own OAuth client. Ros signs people into it.',
  },
  tool: {
    label: 'Tool',
    plural: 'Tools',
    icon: <BuildIcon sx={{ fontSize: 20 }} />,
    blurb: 'A capability inside an app. Inherits the parent app’s sign-in; Ros deep-links to it.',
  },
  public_app: {
    label: 'Public app',
    plural: 'Public apps',
    icon: <PublicIcon sx={{ fontSize: 20 }} />,
    blurb: 'A public destination with no sign-in. Ros just points people to the URL.',
  },
  external_app: {
    label: 'External app',
    plural: 'External apps',
    icon: <LaunchIcon sx={{ fontSize: 20 }} />,
    blurb: 'A third-party app Ros can point people to. No sign-in through Ros.',
  },
  // Catalog-seeded Cap'n Web micro-apps live in D1 as kind=gadget (see seedGadgetRegistry).
  // They are not registered through this admin form (LOADER packages are separate), so gadget is
  // absent from KIND_ORDER below.
  gadget: {
    label: 'Gadget',
    plural: 'Gadgets',
    icon: <WidgetsIcon sx={{ fontSize: 20 }} />,
    blurb: 'A Cap\'n Web micro-app that docks beside chat. Seeded from the platform catalog; imports are per-user.',
  },
}
const KIND_ORDER: RegistryKind[] = ['app', 'tool', 'public_app', 'external_app']
const AUDIENCES: RegistryAudience[] = ['internal', 'external', 'public']

// The empty draft the Register dialog starts from. audience defaults to internal (the common case for
// first-party apps); scopes/redirectUris start empty and only matter for kind=app.
interface Draft {
  kind: RegistryKind
  name: string
  description: string
  url: string
  category: string
  audience: RegistryAudience
  parentAppId: string
  redirectUris: string[]
  allowedScopes: string[]
  // Curated icon NAME (empty string means no icon: the entry falls back to its per-kind default).
  icon: string
}
const emptyDraft = (): Draft => ({
  kind: 'app',
  name: '',
  description: '',
  url: '',
  category: '',
  audience: 'internal',
  parentAppId: '',
  redirectUris: [''],
  allowedScopes: [],
  icon: '',
})

export function RegistryPage() {
  const shell = useRosShell()
  const {authFetch, snapshot} = useRosFeatures()
  const [entries, setEntries] = useState<RegistryEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  // When editingId is set the dialog is in edit mode (the row's id being edited); when it is null and
  // createOpen is true the dialog is in create mode (today's behavior). This one flag distinguishes them.
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft>(emptyDraft)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  // On a successful app registration the provider returns the clientId (and, for a confidential
  // client, a one-time clientSecret). We hold it here to show a copy-once panel after the dialog closes.
  const [created, setCreated] = useState<RegistryCreateResult | null>(null)
  const [credentialDialogMode, setCredentialDialogMode] = useState<'registered' | 'secret-generated'>('registered')
  const [generatingStudioSecret, setGeneratingStudioSecret] = useState(false)
  const [secretError, setSecretError] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<string | null>(null)

  const reload = useCallback(async () => {
    try {
      setEntries(await listRegistryEntries(shell.apiBaseUrl, shell.token, authFetch as typeof fetch))
    } catch {
      // empty state on failure — the list simply shows nothing rather than a scary error wall
    } finally {
      setLoading(false)
    }
  }, [shell.apiBaseUrl, shell.token, authFetch])

  useEffect(() => {
    void reload()
  }, [reload])

  // Apps only — the parent-app selector for a tool draws from these.
  const apps = useMemo(() => entries.filter(e => e.kind === 'app'), [entries])
  const appNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const a of apps) m.set(a.id, a.name)
    return m
  }, [apps])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return entries
    return entries.filter(
      e =>
        e.name.toLowerCase().includes(q) ||
        e.description.toLowerCase().includes(q) ||
        (e.category ?? '').toLowerCase().includes(q) ||
        e.url.toLowerCase().includes(q),
    )
  }, [entries, query])

  const grouped = useMemo(
    () => KIND_ORDER.map(kind => ({ kind, items: visible.filter(e => e.kind === kind) })).filter(g => g.items.length > 0),
    [visible],
  )

  // ── Register / edit dialog ──────────────────────────────────────────
  const openCreate = () => {
    setEditingId(null)
    setDraft(emptyDraft())
    setFormError(null)
    setCreateOpen(true)
  }

  const isEditing = editingId !== null

  // Seed the shared dialog from an existing entry and flip it into edit mode. kind and parentAppId are
  // copied in but render read-only (the backend preserves them). redirectUris and allowedScopes are now
  // editable for an app: the backend re-provisions the live OAuth client when the redirect URIs change.
  const openEdit = (entry: RegistryEntry) => {
    setDraft({
      kind: entry.kind,
      name: entry.name,
      description: entry.description,
      url: entry.url,
      category: entry.category ?? '',
      audience: entry.audience,
      parentAppId: entry.parentAppId ?? '',
      redirectUris: entry.redirectUris?.length ? entry.redirectUris : [''],
      allowedScopes: entry.allowedScopes ?? [],
      icon: entry.icon ?? '',
    })
    setEditingId(entry.id)
    setFormError(null)
    setCreateOpen(true)
  }

  // Close the dialog and reset edit mode so the next Register opens cleanly in create mode.
  const closeDialog = () => {
    setCreateOpen(false)
    setEditingId(null)
  }

  // Client-side validation mirroring the backend: name/description/url/audience always required; a tool
  // needs a parent app; an app needs at least one non-empty redirect URI. Returns an error string or null.
  const validate = (d: Draft): string | null => {
    if (!d.name.trim()) return 'Name is required.'
    if (!d.description.trim()) return 'Description is required — the finder reasons over it.'
    if (!d.url.trim()) return 'URL is required.'
    if (!d.audience) return 'Audience is required.'
    if (d.kind === 'tool' && !d.parentAppId) return 'A tool needs a parent app.'
    if (d.kind === 'app' && !d.redirectUris.some(u => u.trim())) return 'An app needs at least one redirect URI.'
    return null
  }

  const submit = async () => {
    if (submitting) return
    const err = validate(draft)
    if (err) {
      setFormError(err)
      return
    }
    setFormError(null)
    setSubmitting(true)

    // Edit mode: send the editable fields. The backend preserves id, kind, clientId, and parentAppId
    // from the existing row, so we never send those. For an app we also send redirectUris and
    // allowedScopes: a redirectUris change re-provisions the live OAuth client server-side.
    if (editingId) {
      const patch: RegistryEntryUpdate = {
        name: draft.name.trim(),
        description: draft.description.trim(),
        url: draft.url.trim(),
        audience: draft.audience,
        ...(draft.category.trim() ? { category: draft.category.trim() } : {}),
        // Always send icon so clearing the picker explicitly nulls it out (null = clear, undefined = keep).
        icon: draft.icon || null,
        ...(draft.kind === 'app'
          ? {
              redirectUris: draft.redirectUris.map(u => u.trim()).filter(Boolean),
              allowedScopes: draft.allowedScopes,
            }
          : {}),
      }
      try {
        await updateRegistryEntry(shell.apiBaseUrl, shell.token, editingId, patch, authFetch as typeof fetch)
        closeDialog()
        await reload()
      } catch (e) {
        setFormError(e instanceof Error ? e.message : 'Update failed.')
      } finally {
        setSubmitting(false)
      }
      return
    }

    const input: RegistryEntryInput = {
      kind: draft.kind,
      name: draft.name.trim(),
      description: draft.description.trim(),
      url: draft.url.trim(),
      audience: draft.audience,
      ...(draft.category.trim() ? { category: draft.category.trim() } : {}),
      ...(draft.icon ? { icon: draft.icon } : {}),
      ...(draft.kind === 'tool' ? { parentAppId: draft.parentAppId } : {}),
      ...(draft.kind === 'app'
        ? {
            redirectUris: draft.redirectUris.map(u => u.trim()).filter(Boolean),
            allowedScopes: draft.allowedScopes,
          }
        : {}),
    }

    try {
      const result = await createRegistryEntry(shell.apiBaseUrl, shell.token, input, authFetch as typeof fetch)
      setCreateOpen(false)
      // Only an app returns a clientId worth surfacing; show the copy-once panel for it.
      if (result.kind === 'app' && result.clientId) {
        setCredentialDialogMode('registered')
        setCreated(result)
      }
      await reload()
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Registration failed.')
    } finally {
      setSubmitting(false)
    }
  }

  const remove = async (id: string) => {
    if (pendingDelete) return
    if (!window.confirm('Remove this registry entry? Ros will no longer route people to it.')) return
    setPendingDelete(id)
    // Optimistic removal; resync from the server on failure.
    setEntries(prev => prev.filter(e => e.id !== id))
    try {
      await deleteRegistryEntry(shell.apiBaseUrl, shell.token, id, authFetch as typeof fetch)
    } catch {
      await reload()
    } finally {
      setPendingDelete(null)
    }
  }

  const generateStudioSecret = async (entry: RegistryEntry) => {
    if (entry.id !== 'app-studio' || generatingStudioSecret) return
    const confirmed = window.confirm(
      'Generate a new Rival Studio client secret?\n\n' +
      'Any previous secret stops working immediately. Copy the new value into this environment’s Studio SSM configuration before using Sign in with Ros.',
    )
    if (!confirmed) return

    setSecretError(null)
    setGeneratingStudioSecret(true)
    try {
      const credentials = await generateStudioClientSecret(
        shell.apiBaseUrl,
        shell.token,
        authFetch as typeof fetch,
      )
      setCredentialDialogMode('secret-generated')
      setCreated({...entry, ...credentials})
    } catch (e) {
      setSecretError(e instanceof Error ? e.message : 'Studio client secret generation failed.')
    } finally {
      setGeneratingStudioSecret(false)
    }
  }

  const toggleScope = (scope: string) => {
    setDraft(d => ({
      ...d,
      allowedScopes: d.allowedScopes.includes(scope)
        ? d.allowedScopes.filter(s => s !== scope)
        : [...d.allowedScopes, scope],
    }))
  }

  const setRedirectUri = (i: number, value: string) => {
    setDraft(d => ({ ...d, redirectUris: d.redirectUris.map((u, idx) => (idx === i ? value : u)) }))
  }
  const addRedirectUri = () => setDraft(d => ({ ...d, redirectUris: [...d.redirectUris, ''] }))
  const removeRedirectUri = (i: number) =>
    setDraft(d => ({ ...d, redirectUris: d.redirectUris.filter((_, idx) => idx !== i) }))

  const copy = (text: string) => {
    void navigator.clipboard?.writeText(text)
  }

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto', px: 4, py: 5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h3" sx={SERIF_HEADING}>App registry</Typography>
          <Typography sx={{ color: 'text.secondary', mt: 0.5, maxWidth: 660 }}>
            The apps, tools, and public destinations Ros can route people to. Each entry’s description is what the
            finder reasons over, so write it for a person deciding where to go.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ flexShrink: 0, mt: 0.5 }}>
          Register
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search registry"
          sx={{ width: 320 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Typography sx={{ ml: 'auto', color: 'text.secondary', fontSize: '0.8rem' }}>
          {visible.length} {visible.length === 1 ? 'entry' : 'entries'}
        </Typography>
      </Box>

      {secretError && (
        <Alert severity="error" onClose={() => setSecretError(null)} sx={{mb: 2}}>
          {secretError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: 'primary.main' }} />
        </Box>
      ) : grouped.length === 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: 2.5, p: 6, textAlign: 'center' }}>
          <Box sx={{ width: 48, height: 48, mx: 'auto', borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,143,168,0.10)', color: 'primary.main' }}>
            <WidgetsIcon />
          </Box>
          <Typography sx={{ fontWeight: 600, mt: 1.5 }}>
            {query.trim() ? `No entries match “${query.trim()}”` : 'No entries yet'}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', mt: 0.5 }}>
            {query.trim() ? 'Try a different search.' : 'Register an app, tool, or public destination for Ros to route people to.'}
          </Typography>
        </Paper>
      ) : (
        grouped.map(group => (
          <Box key={group.kind} sx={{ mb: 3.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{ color: 'primary.main', display: 'flex' }}>{KIND_META[group.kind].icon}</Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{KIND_META[group.kind].plural}</Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>({group.items.length})</Typography>
            </Box>
            <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
              {group.items.map((e, i) => (
                <Box
                  key={e.id}
                  sx={{ px: 2.5, py: 1.75, borderTop: i === 0 ? 'none' : '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' }, '&:hover .row-actions': { opacity: 1 } }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>{e.name}</Typography>
                        <Chip label={e.audience} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
                        {e.category && <Chip label={e.category} size="small" sx={{ height: 20, fontSize: '0.68rem', bgcolor: 'rgba(0,143,168,0.10)', color: 'primary.main' }} />}
                        {e.kind === 'tool' && e.parentAppId && (
                          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                            in {appNameById.get(e.parentAppId) ?? e.parentAppId}
                          </Typography>
                        )}
                      </Box>
                      <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mt: 0.5 }}>{e.description}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75, minWidth: 0 }}>
                        <LaunchIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        <Typography
                          component="a"
                          href={e.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          noWrap
                          sx={{ fontSize: '0.78rem', color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {e.url}
                        </Typography>
                      </Box>
                      {e.kind === 'app' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                          {e.clientId && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Client ID</Typography>
                              <Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', bgcolor: 'action.hover', px: 0.75, py: 0.25, borderRadius: 0.75 }}>{e.clientId}</Typography>
                              <Tooltip title="Copy client ID">
                                <IconButton size="small" onClick={() => copy(e.clientId!)}><ContentCopyIcon sx={{ fontSize: 13 }} /></IconButton>
                              </Tooltip>
                            </Box>
                          )}
                          {snapshot.userType === 'internal' && e.id === 'app-studio' && e.clientId && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={generatingStudioSecret
                                ? <CircularProgress size={13} />
                                : <VpnKeyOutlinedIcon sx={{fontSize: 15}} />}
                              disabled={generatingStudioSecret}
                              onClick={() => void generateStudioSecret(e)}
                              sx={{fontSize: '0.7rem', py: 0.2, textTransform: 'none'}}
                            >
                              Generate new client secret
                            </Button>
                          )}
                          {e.allowedScopes.map(s => (
                            <Chip key={s} label={s} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.64rem', fontFamily: 'monospace' }} />
                          ))}
                        </Box>
                      )}
                    </Box>
                    <Box className="row-actions" sx={{ opacity: 0, transition: 'opacity 0.15s', flexShrink: 0, display: 'flex', gap: 0.25 }}>
                      <Tooltip title="Edit">
                        <span>
                          <IconButton size="small" onClick={() => openEdit(e)} sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <span>
                          <IconButton size="small" onClick={() => void remove(e.id)} disabled={pendingDelete === e.id} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Paper>
          </Box>
        ))
      )}

      {/* ── Register dialog ── */}
      <Dialog open={createOpen} onClose={() => !submitting && closeDialog()} fullWidth maxWidth="sm" transitionDuration={0}>
        <DialogTitle sx={SERIF_HEADING}>{isEditing ? `Edit ${KIND_META[draft.kind].label.toLowerCase()}` : 'Register an entry'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          {formError && <Alert severity="error" sx={{ mb: 0.5 }}>{formError}</Alert>}

          <TextField
            select
            label="Kind"
            fullWidth
            size="small"
            value={draft.kind}
            disabled={isEditing}
            onChange={e => setDraft(d => ({ ...d, kind: e.target.value as RegistryKind }))}
            helperText={isEditing ? 'Read-only. Re-register to change.' : undefined}
          >
            {KIND_ORDER.map(k => (
              <MenuItem key={k} value={k}>{KIND_META[k].label}</MenuItem>
            ))}
          </TextField>
          <Typography sx={{ mt: -1, fontSize: '0.75rem', color: 'text.secondary' }}>{KIND_META[draft.kind].blurb}</Typography>

          <TextField
            autoFocus
            label="Name"
            fullWidth
            size="small"
            value={draft.name}
            onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
            placeholder="e.g. Rival Studio"
          />
          <TextField
            label="Description"
            fullWidth
            size="small"
            multiline
            minRows={3}
            value={draft.description}
            onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
            placeholder="Plain-language: what it does and when someone would want it. Ros reads this to decide where to route people."
            helperText="The finder reasons over this. Write it for a person choosing where to go."
          />
          <TextField
            label="URL"
            fullWidth
            size="small"
            value={draft.url}
            onChange={e => setDraft(d => ({ ...d, url: e.target.value }))}
            placeholder="https://…"
          />
          <TextField
            label="Category"
            fullWidth
            size="small"
            value={draft.category}
            onChange={e => setDraft(d => ({ ...d, category: e.target.value }))}
            placeholder="Optional — e.g. analysis, utility, platform"
          />

          {/* Icon picker: a curated set plus a Default (None) option. Empty icon means the entry falls
              back to its per-kind default icon, so "Default" is the state when nothing is chosen. */}
          <Box>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, mb: 0.75 }}>Icon</Typography>
            <ToggleButtonGroup
              value={draft.icon}
              exclusive
              onChange={(_e, value) => {
                // ToggleButtonGroup passes null when the active button is clicked again. Treat that,
                // and the explicit 'Default' button, as clearing the icon (empty string).
                if (value === null || value === 'default') setDraft(d => ({ ...d, icon: '' }))
                else setDraft(d => ({ ...d, icon: value as string }))
              }}
              sx={{ flexWrap: 'wrap', gap: 0.75, '& .MuiToggleButtonGroup-grouped': { border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', mx: 0 } }}
            >
              <ToggleButton value="default" selected={draft.icon === ''} sx={{ px: 1.25, py: 0.75, textTransform: 'none', color: 'text.secondary', '&.Mui-selected': { bgcolor: 'rgba(0,143,168,0.12)', color: 'primary.main' } }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>Default</Typography>
              </ToggleButton>
              {CURATED_ICONS.map(opt => (
                <ToggleButton
                  key={opt.name}
                  value={opt.name}
                  aria-label={opt.label}
                  sx={{ p: 0.75, minWidth: 40, color: 'text.secondary', '&.Mui-selected': { bgcolor: 'rgba(0,143,168,0.12)', color: 'primary.main' } }}
                >
                  <Tooltip title={opt.label}>
                    <Box sx={{ display: 'flex' }}>{resolveIcon(opt.name, <></>, 'small')}</Box>
                  </Tooltip>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.75 }}>
              Optional. Default uses the per-kind icon (app, tool, or public).
            </Typography>
          </Box>

          <TextField
            select
            label="Audience"
            fullWidth
            size="small"
            value={draft.audience}
            onChange={e => setDraft(d => ({ ...d, audience: e.target.value as RegistryAudience }))}
          >
            {AUDIENCES.map(a => (
              <MenuItem key={a} value={a}>{a}</MenuItem>
            ))}
          </TextField>

          {/* kind=tool: parent app selector, drawn from registered apps. */}
          {draft.kind === 'tool' && (
            <TextField
              select
              label="Parent app"
              fullWidth
              size="small"
              value={draft.parentAppId}
              disabled={isEditing}
              onChange={e => setDraft(d => ({ ...d, parentAppId: e.target.value }))}
              helperText={isEditing ? 'Read-only. Re-register to change.' : apps.length === 0 ? 'Register an app first — a tool must belong to one.' : 'The app this tool belongs to (it inherits that app’s sign-in).'}
            >
              {apps.map(a => (
                <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
              ))}
            </TextField>
          )}

          {/* kind=app: redirect URIs + scope checkboxes for its OAuth client. */}
          {draft.kind === 'app' && (
            <>
              <Box>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, mb: 0.75 }}>Redirect URIs</Typography>
                {draft.redirectUris.map((uri, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={uri}
                      onChange={e => setRedirectUri(i, e.target.value)}
                      placeholder="https://app.example.com/auth/ros/callback"
                    />
                    {draft.redirectUris.length > 1 && (
                      <IconButton size="small" onClick={() => removeRedirectUri(i)} sx={{ color: 'text.secondary' }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
                <Button size="small" startIcon={<AddIcon />} onClick={addRedirectUri} sx={{ textTransform: 'none' }}>
                  Add redirect URI
                </Button>
                {isEditing && (
                  <Typography sx={{ fontSize: '0.75rem', color: 'primary.main', mt: 0.75 }}>
                    Changing redirect URIs re-provisions the live OAuth client.
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, mb: 0.5 }}>Allowed scopes</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.5 }}>
                  The ceiling this app’s client may request. It can ask for these and no more.
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                  {KNOWN_SCOPES.map(scope => (
                    <FormControlLabel
                      key={scope}
                      control={<Checkbox size="small" checked={draft.allowedScopes.includes(scope)} onChange={() => toggleScope(scope)} />}
                      label={<Typography sx={{ fontSize: '0.82rem', fontFamily: 'monospace' }}>{scope}</Typography>}
                    />
                  ))}
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={() => void submit()} disabled={submitting} sx={{ textTransform: 'none', fontWeight: 600 }}>
            {isEditing ? (submitting ? 'Saving…' : 'Save changes') : (submitting ? 'Registering…' : 'Register')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── New-app credentials panel (shown once after registering an app) ── */}
      <Dialog open={!!created} onClose={() => setCreated(null)} fullWidth maxWidth="sm" transitionDuration={0}>
        <DialogTitle sx={SERIF_HEADING}>
          {credentialDialogMode === 'secret-generated' ? 'New Rival Studio client secret' : 'App registered'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.88rem' }}>
            {credentialDialogMode === 'secret-generated' ? (
              <>Copy these values into the matching Studio environment’s SSM configuration. The client ID remains visible on this entry; the new secret is shown only now.</>
            ) : (
              <>Wire these into <strong>{created?.name}</strong>. The client ID is always visible on the entry; the secret (if any) is shown only now.</>
            )}
          </Typography>
          {created?.clientId && (
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, mb: 0.5 }}>Client ID</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '0.82rem', fontFamily: 'monospace', bgcolor: 'action.hover', px: 1, py: 0.75, borderRadius: 1, flex: 1, wordBreak: 'break-all' }}>{created.clientId}</Typography>
                <IconButton size="small" onClick={() => created.clientId && copy(created.clientId)}><ContentCopyIcon fontSize="small" /></IconButton>
              </Box>
            </Box>
          )}
          {created?.clientSecret && (
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, mb: 0.5 }}>Client secret</Typography>
              <Alert severity="warning" sx={{ mb: 1, py: 0.25 }}>Copy this now. It is not shown again.</Alert>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '0.82rem', fontFamily: 'monospace', bgcolor: 'action.hover', px: 1, py: 0.75, borderRadius: 1, flex: 1, wordBreak: 'break-all' }}>{created.clientSecret}</Typography>
                <IconButton size="small" onClick={() => created.clientSecret && copy(created.clientSecret)}><ContentCopyIcon fontSize="small" /></IconButton>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCreated(null)} sx={{ textTransform: 'none', fontWeight: 600 }}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
